/**
 * aparencia.ts — A LISTA ÚNICA do que se pode escolher na aparência do app.
 *
 * POR QUE ESTE ARQUIVO EXISTE. Até 28/08/2026 havia duas listas escritas à mão,
 * e nenhuma sabia da outra: o painel da paleta (`Theming.tsx`) oferecia 16 cores
 * e os quatro blocos de cabeçalho e menu; a página de preferências oferecia 14
 * cores e o plano de fundo. Quem escolhesse `chocolate` ou `pista` no painel não
 * conseguia reencontrar a escolha na página — ela simplesmente não estava lá.
 *
 * A regra que isso passa a cumprir: **a escolha é uma só, as apresentações é que
 * são duas.** O painel lateral é o atalho do meio do expediente; a página é onde
 * há largura para rótulo. Nenhum dos dois inventa opção.
 *
 * ONDE ESTA LISTA NÃO MANDA: o efeito de cada opção é do tema (as classes
 * `theme-*`, `bg-*` e `bg-gradient-*` vivem em `assets/scss/`, que não se edita).
 * Aqui está só o CATÁLOGO — o que existe, como se chama em português e qual é a
 * amostra que se mostra. Ver `.claude/docs/projeto/11-convencao-visual.md`.
 *
 * RTL FICOU DE FORA de propósito: o `ThemeContext` continua sabendo virar o
 * sentido do texto, mas isso não é escolha de ninguém aqui — é recurso do tema
 * para quem escreve em árabe ou hebraico, e ocupava um bloco inteiro do painel.
 */
import type {
  BackgroundGradient,
  ColorTheme,
  HeaderLayout,
  HeaderStyle,
  SidebarLayout,
  SidebarStyle,
} from '@/contexts/ThemeContext';

/**
 * Uma opção de COR — a amostra é um quadrado/bolinha pintado.
 *
 * `amostra` é a classe da bolinha na página (`bg-azul`); o painel usa outro
 * caminho para a mesma cor (`theme-{id}` sobre `bg-theme-1`), porque ali a
 * amostra mostra o gradiente do tema, não a cor chapada. Duas apresentações da
 * MESMA escolha — que é justamente o motivo deste arquivo.
 */
export interface OpcaoCor {
  id: ColorTheme;
  nome: string;
  amostra: string;
}

/** Uma opção de FORMA — a amostra é um ícone. */
export interface OpcaoForma<T> {
  id: T;
  nome: string;
  icone: string;
}

/** Uma opção de PREENCHIMENTO — a amostra é a própria classe de fundo. */
export interface OpcaoPreenchimento<T> {
  id: T;
  nome: string;
  amostra: string;
}

/**
 * As 16 cores do tema.
 *
 * `default` não tem `theme-default` no SCSS de propósito — ele é a AUSÊNCIA de
 * classe, o roxo de fábrica do AdminUX. Por isso a amostra dele leva o ícone de
 * voltar ao padrão em vez de cor, e é o único caso especial da lista.
 */
export const CORES: OpcaoCor[] = [
  { id: 'default', nome: 'Padrão', amostra: 'bg-default' },
  { id: 'blue', nome: 'Azul', amostra: 'bg-blue' },
  { id: 'indigo', nome: 'Índigo', amostra: 'bg-indigo' },
  { id: 'purple', nome: 'Roxo', amostra: 'bg-purple' },
  { id: 'pink', nome: 'Rosa', amostra: 'bg-pink' },
  { id: 'red', nome: 'Vermelho', amostra: 'bg-red' },
  { id: 'orange', nome: 'Laranja', amostra: 'bg-orange' },
  { id: 'yellow', nome: 'Amarelo', amostra: 'bg-yellow' },
  { id: 'green', nome: 'Verde', amostra: 'bg-green' },
  // Nome de UMA palavra, como todos os outros: com "Verde-azulado" a célula
  // ficava mais larga que as vizinhas e a fila de bolinhas desalinhava.
  { id: 'teal', nome: 'Turquesa', amostra: 'bg-teal' },
  { id: 'cyan', nome: 'Ciano', amostra: 'bg-cyan' },
  // As duas que só existiam no painel, e por isso sumiam de vista na página.
  { id: 'chocolate', nome: 'Chocolate', amostra: 'bg-chocolate' },
  { id: 'pista', nome: 'Pistache', amostra: 'bg-pista' },
  { id: 'brown', nome: 'Marrom', amostra: 'bg-brown' },
  { id: 'grey', nome: 'Cinza', amostra: 'bg-grey' },
  // `bg-black` não existe no SCSS do tema; o preto se pinta com `bg-dark`.
  { id: 'black', nome: 'Preto', amostra: 'bg-dark' },
];

/**
 * Os 13 planos de fundo. O `id` JÁ É a classe — não há de-para aqui.
 *
 * A página oferecia 6 dos 10 gradientes; os quatro últimos existem no
 * `ThemeContext` desde sempre e nunca tinham aparecido em tela nenhuma.
 */
export const FUNDOS: OpcaoPreenchimento<BackgroundGradient>[] = [
  { id: 'bg-default', nome: 'Nenhum', amostra: 'bg-default' },
  { id: 'bg-white', nome: 'Branco', amostra: 'bg-white' },
  { id: 'bg-r-gradient', nome: 'Do tema', amostra: 'bg-r-gradient' },
  { id: 'bg-gradient-1', nome: 'Suave 1', amostra: 'bg-gradient-1' },
  { id: 'bg-gradient-2', nome: 'Suave 2', amostra: 'bg-gradient-2' },
  { id: 'bg-gradient-3', nome: 'Suave 3', amostra: 'bg-gradient-3' },
  { id: 'bg-gradient-4', nome: 'Suave 4', amostra: 'bg-gradient-4' },
  { id: 'bg-gradient-5', nome: 'Suave 5', amostra: 'bg-gradient-5' },
  { id: 'bg-gradient-6', nome: 'Suave 6', amostra: 'bg-gradient-6' },
  { id: 'bg-gradient-7', nome: 'Suave 7', amostra: 'bg-gradient-7' },
  { id: 'bg-gradient-8', nome: 'Suave 8', amostra: 'bg-gradient-8' },
  { id: 'bg-gradient-9', nome: 'Suave 9', amostra: 'bg-gradient-9' },
  { id: 'bg-gradient-10', nome: 'Suave 10', amostra: 'bg-gradient-10' },
];

/**
 * PREENCHIMENTO do cabeçalho e do menu — a mesma escala para os dois, porque no
 * tema eles são a mesma escala. Os rótulos saem do inglês do AdminUX
 * (`Transparent`, `BgTheme`, `Accent`), que nunca foi traduzido.
 */
// Rótulos de UMA palavra: a célula tem largura fixa (`.casaa-escolha`), e nome
// comprido truncava com reticências. O nome inteiro, quando precisa, vai no
// `title` — o balãozinho do mouse.
const PREENCHIMENTOS = [
  // "Transparente" não cabia na célula e truncava. "Sem cor" diz a mesma
  // coisa e é o que a pessoa vê: o fundo da página aparecendo por trás.
  { nome: 'Sem cor', amostra: '' },
  { nome: 'Branco', amostra: 'bg-white' },
  { nome: 'Fundo', amostra: 'bg-body-1' },
  { nome: 'Tema', amostra: 'bg-theme-1' },
  { nome: 'Destaque', amostra: 'bg-theme-accent-1' },
] as const;

export const ESTILOS_CABECALHO: OpcaoPreenchimento<HeaderStyle>[] = (
  ['transparent', 'white', 'bg', 'theme', 'accent'] as HeaderStyle[]
).map((id, i) => ({ id, ...PREENCHIMENTOS[i] }));

export const ESTILOS_MENU: OpcaoPreenchimento<SidebarStyle>[] = (
  ['transparent', 'white', 'bg', 'theme', 'accent'] as SidebarStyle[]
).map((id, i) => ({ id, ...PREENCHIMENTOS[i] }));

/**
 * FORMATO do cabeçalho e do menu.
 *
 * OS ÍCONES MUDARAM em 28/08/2026. O painel do tema usava `bi-arrow-clockwise`
 * — a SETA DE RECARREGAR — para a opção "padrão" das duas listas. Num painel
 * sem rótulo, onde as amostras são só tooltip, aquilo se lia como um botão de
 * desfazer, não como uma escolha. Trocados por ícones que dizem o que a opção
 * faz. Todos conferidos no navegador: classe de ícone que não existe não dá
 * erro, o glifo só não aparece.
 */
export const LAYOUTS_CABECALHO: OpcaoForma<HeaderLayout>[] = [
  { id: 'standard', nome: 'Cheio', icone: 'bi bi-arrows-angle-expand' },
  { id: 'boxed', nome: 'Em caixa', icone: 'bi bi-bounding-box' },
];

/** `iconic` é o menu recolhido, só com a coluna de ícones. */
export const LAYOUTS_MENU: OpcaoForma<SidebarLayout>[] = [
  { id: 'standard', nome: 'Cheio', icone: 'bi bi-layout-sidebar' },
  { id: 'boxed', nome: 'Em caixa', icone: 'bi bi-bounding-box' },
  { id: 'iconic', nome: 'Só ícones', icone: 'bi bi-list' },
];

/**
 * TAMANHOS DA MARCA — a altura do nome CASAA ao lado do símbolo redondo.
 *
 * Os seis valores vêm da bancada `/dev/header` (fatia 2, 26/08/2026), onde ele
 * os comparou ao vivo antes de cravar 26 no CSS. Em 28/08 a escolha saiu da
 * bancada e virou preferência de cada um.
 *
 * O símbolo redondo mede 32px e o círculo desenhado dentro dele, ~20px — por
 * isso 36 fica desproporcional e 22 some ao lado da bola. A referência é a
 * linha do meio: o meio do "CASAA" deve cair no meio da bolinha.
 */
export const TAMANHOS_MARCA: { px: number; nome: string }[] = [
  { px: 22, nome: 'Menor' },
  { px: 24, nome: 'Pequeno' },
  { px: 26, nome: 'Padrão' },
  { px: 28, nome: 'Médio' },
  { px: 32, nome: 'Grande' },
  { px: 36, nome: 'Maior' },
];
